import React from "react";
import { Link } from "react-router-dom";
import "./Locations.css";

const PinIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--amber)"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const regions = [
  {
    id: "south-africa",
    country: "South Africa",
    eyebrow: "The Secretariat",
    heading: "Strategic Leadership & Financial Oversight",
    image: "/images/gallery/volunteering-02.jpg",
    intro:
      "The South African Secretariat is the governance backbone of the Food and Nutrition Foundation. While the deepest grassroots work happens in Zambia and Malawi, our South African team provides the strategic leadership, research, and rigorous financial oversight that allow grassroots implementation to be governed with absolute transparency. This dual-layer structure — local trust paired with administrative rigour — reduces risk for our donors while keeping every Rand and every Kwacha accountable.",
    projects: [
      {
        title: "Secretariat Operations",
        desc: "Strategic planning, donor relations, and financial governance for all FNF regions are coordinated from our South African base, ensuring every programme is accountable and transparent.",
      },
      {
        title: "Community Outreach & Awareness",
        desc: "Our South African team takes part in community safety initiatives, including standing alongside the Gender-Based Violence Brigade, and partners with organisations like Food Forward SA for events such as Mandela Day.",
      },
      {
        title: "Monitoring & Evaluation",
        desc: "The Secretariat manages the rigorous monitoring and evaluation framework that tracks not just outputs — like meals served — but long-term outcomes such as literacy gains and sustained economic independence.",
      },
    ],
  },
  {
    id: "zambia",
    country: "Zambia",
    eyebrow: "Where It All Began",
    heading: "The Flagship Hubs of Kamanga, Kimale & Lunga",
    image: "/images/gallery/mom-child-01.jpg",
    intro:
      "Founded in late 2008 and officially registered in early 2009, FNF's work started as a local response to hunger in Kamanga Compound, Lusaka. Today, Zambia is home to our flagship Community Centres — permanent, physical anchors that allow whole communities to move from vulnerability to long-term resilience, addressing nutrition, education, and economic agency together in one place.",
    projects: [
      {
        title: "Kamanga Centre (Lusaka)",
        desc: "Our flagship hub, providing feeding programmes, early childhood education, and skills training. It is the centre where it all started, and remains the heart of our daily operations.",
      },
      {
        title: "Kimale Centre (Solwezi)",
        desc: "A priority infrastructure project focused on youth empowerment and women's livelihoods, extending our reach into Zambia's Copperbelt region.",
      },
      {
        title: "Lunga Centre (Luapula)",
        desc: "A rural hub supporting agricultural initiatives, sustainable farming, climate resilience, and nutrition education — reachable in part by the river routes that connect Luapula's remote communities.",
      },
      {
        title: "Home-Based Care & Housing",
        desc: "Trained caregivers visit people living with HIV/AIDS every week, while our housing programme has built permanent two-room homes for widows and grandmothers raising orphaned grandchildren in unsafe shelters.",
      },
    ],
  },
  {
    id: "malawi",
    country: "Malawi",
    eyebrow: "Our Newest Frontier",
    heading: "Scaling the Hub Model Through Partnership",
    image: "/images/gallery/eating-children-04.jpg",
    intro:
      "Malawi represents the newest chapter in FNF's regional growth. Rather than building from scratch, we are scaling our proven Hub model through collaborative partnerships with community organisations already embedded on the ground — extending our reach across Southern Africa while staying true to the dignity-first principles that have guided our work since 2008.",
    projects: [
      {
        title: "Hub Model Expansion",
        desc: "We are partnering with local community organisations to bring the integrated Hub approach — nutrition, education, and economic empowerment under one roof — to communities across Malawi.",
      },
      {
        title: "Regional Governance",
        desc: "As in Zambia, our Malawi partnerships are supported by the South African Secretariat's strategic oversight, ensuring the same standard of transparency and accountability from day one.",
      },
      {
        title: "Building on 17+ Years of Trust",
        desc: "Malawi communities benefit from nearly two decades of refined, on-the-ground experience — a model already proven in Zambia, now adapted collaboratively for new partners and new contexts.",
      },
    ],
  },
];

export default function Locations() {
  return (
    <main>
      {/* HERO */}
      <section
        className="page-hero"
        aria-label="Where We Are Located page hero"
      >
        <div
          className="page-hero-bg"
          style={{
            backgroundImage: "url('/images/gallery/lunga-boat-01.jpg')",
          }}
        />
        <div className="container page-hero-content">
          <span className="eyebrow" style={{ color: "var(--amber-light)" }}>
            Zambia · South Africa · Malawi
          </span>
          <h1>Where We Are Located</h1>
          <p>
            A regional impact engine built on permanent infrastructure and local
            trust — from the flagship hubs of Zambia, to the strategic
            Secretariat in South Africa, to our newest partnerships in Malawi.
          </p>
        </div>
      </section>

      {/* INTRO */}
      <section
        className="section locations-intro-section"
        aria-labelledby="locations-intro-heading"
      >
        <div className="container">
          <div className="section-title">
            <span className="eyebrow">A Regional Model</span>
            <h2 id="locations-intro-heading">One Mission, Three Countries</h2>
            <div className="divider divider-center" />
            <p>
              Unlike traditional, fragmented aid models, FNF utilises an
              integrated "Hub" ecosystem that addresses the root causes of
              poverty — nutrition, education, and economic agency —
              simultaneously, within a single physical location. By 2030, we aim
              for strong communities linked by these key hubs, expanding our
              influence from cities to even the most remote rural areas.
            </p>
          </div>
        </div>
      </section>

      {/* REGION SECTIONS */}
      {regions.map((region, i) => (
        <section
          key={region.id}
          id={region.id}
          className={`section region-section${i % 2 === 1 ? " region-section-alt" : ""}`}
          aria-labelledby={`${region.id}-heading`}
        >
          <div className="container">
            <div className={`region-grid${i % 2 === 1 ? " reverse" : ""}`}>
              <div className="region-image">
                <img
                  src={region.image}
                  alt={`Our work in ${region.country}`}
                  loading="lazy"
                />
                <div className="region-image-tag">
                  <PinIcon />
                  <span>{region.country}</span>
                </div>
              </div>
              <div className="region-content">
                <span className="eyebrow">{region.eyebrow}</span>
                <h2 id={`${region.id}-heading`}>{region.heading}</h2>
                <div className="divider" />
                <p className="region-intro-text">{region.intro}</p>

                <div className="region-projects">
                  {region.projects.map((p, idx) => (
                    <div className="region-project" key={idx}>
                      <div className="region-project-icon">
                        <CheckIcon />
                      </div>
                      <div>
                        <h3>{p.title}</h3>
                        <p>{p.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Link
                  to={`/gallery?location=${encodeURIComponent(region.country)}`}
                  className="btn btn-outline-green"
                  style={{ marginTop: "1.5rem" }}
                >
                  See Photos From {region.country}
                </Link>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="cta-band" aria-label="Support CTA">
        <div className="container cta-band-inner">
          <div>
            <h2>One Foundation, Three Countries, One Mission</h2>
            <p>Help us deepen our roots in every community we serve.</p>
          </div>
          <div className="cta-band-actions">
            <Link to="/donate" className="btn btn-primary btn-lg">
              Donate Now
            </Link>
            <Link to="/get-involved" className="btn btn-secondary">
              Get Involved
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
